import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import InputOtp10 from '@/components/ui/input-otp-10';
import {
  getMfaTrustVersion,
  saveMfaDeviceTrust,
} from '@/utils/mfaDeviceTrust';
import type { User } from '@supabase/supabase-js';

type MfaChallengeFormProps = {
  user: User;
  onVerified: () => void;
  /** True while the screen slides away after verification succeeds. */
  leaving?: boolean;
};

export function MfaChallengeForm({ user, onVerified, leaving = false }: MfaChallengeFormProps) {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(true);

  const handleComplete = async (code: string) => {
    setError(null);
    setIsVerifying(true);
    try {
      const factors = await supabase.auth.mfa.listFactors();
      if (factors.error) throw factors.error;
      const totpFactor = factors.data.totp.find((f) => f.status === 'verified') || factors.data.totp[0];
      if (!totpFactor) throw new Error('No authenticator factor found on this account.');

      const challenge = await supabase.auth.mfa.challenge({ factorId: totpFactor.id });
      if (challenge.error) throw challenge.error;

      const verify = await supabase.auth.mfa.verify({
        factorId: totpFactor.id,
        challengeId: challenge.data.id,
        code,
      });
      if (verify.error) throw verify.error;

      if (rememberDevice) {
        saveMfaDeviceTrust(user.id, getMfaTrustVersion(user));
      }
      setIsSuccess(true);
      onVerified();
    } catch (e) {
      setError((e as Error)?.message || 'Invalid authenticator code.');
      setValue('');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className={`min-h-screen flex items-center justify-center p-6 bg-slate-100 dark:bg-slate-950 ${leaving ? 'animate-slide-out-right' : ''}`}>
      <InputOtp10
        value={value}
        onChange={setValue}
        onComplete={(code) => void handleComplete(code)}
        isVerifying={isVerifying}
        isSuccess={isSuccess}
        error={error}
        title="Authenticator code"
        description="Enter the 6-digit code from your authenticator app to continue."
        successTitle="Verified"
        successDescription="You are signed in. Loading your workspace…"
        rememberDevice={rememberDevice}
        onRememberDeviceChange={setRememberDevice}
        showRememberDevice
      />
    </div>
  );
}
